import { useContext, useCallback } from 'react';
import { UserContext } from '../../context/UserContext';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import { getEnvironmentAsEnum } from '../../utils/environment';
import SectionButton from '../common/SectionButton';
import { getButtonEventPayload } from '../../analytics/util';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../../utils/constants';
import { championsAuthSignupIsActive } from '../../services/statsig/featureGates';

// Internal route for champions signup page
const CHAMPIONS_SIGNUP_PATH = '/champions/signup';

interface AuthGatedApplyButtonProps {
  /** Unique identifier for analytics - e.g., 'championsHeroButton' or 'championsAwardsButton' */
  buttonId: string;
  /** Section identifier for analytics - e.g., 'championsHeroSection' or 'championsAwardsSection' */
  sectionId: string;
}

/**
 * Apply Now button with feature-flagged authentication.
 * 
 * When championsAuthSignupIsActive feature gate is ENABLED:
 * - If user is logged in: redirects to internal champions signup page
 * - If user is not logged in: redirects to login page, then to champions signup page after authentication
 * 
 * When championsAuthSignupIsActive feature gate is DISABLED:
 * - Redirects to external Salesforce signup page (old flow)
 */
export const AuthGatedApplyButton = ({ buttonId, sectionId }: AuthGatedApplyButtonProps) => {
  const userData = useContext(UserContext);
  const user = userData?.user;
  const isLoggedIn = !!user;
  const currentEnv = getEnvironmentAsEnum();
  const isAuthSignupEnabled = championsAuthSignupIsActive();

  // Full URL for the signup page (used for login redirect)
  const signupFullUrl = `${process.env.HOME_BASE_URL}${CHAMPIONS_SIGNUP_PATH}`;

  // Determine the href based on feature flag and login status
  const getHref = useCallback(() => {
    // If feature flag is disabled, use the old external URL flow
    if (!isAuthSignupEnabled) {
      return DEFAULT_CHAMPIONS_SIGNUP_URL;
    }

    // Feature flag is enabled - use auth-gated flow
    if (isLoggedIn) {
      // User is logged in, redirect to internal signup page
      return CHAMPIONS_SIGNUP_PATH;
    } else {
      // User is not logged in, redirect to login with signup page as the post-login destination
      return getCommunityLoginUrl(
        {
          redirectUrl: signupFullUrl,
          experience: 'entry-auth',
        },
        currentEnv,
      );
    }
  }, [isAuthSignupEnabled, isLoggedIn, currentEnv, signupFullUrl]);

  const href = getHref();

  // Use _blank for external URL, _self for internal navigation
  const target = isAuthSignupEnabled ? '_self' : '_blank';

  return (
    <SectionButton
      href={href}
      text="Apply now"
      target={target}
      hasArrow={false}
      clickEventPayload={getButtonEventPayload('clicked', buttonId, sectionId, href, 'Apply now')}
      hoverEventPayload={getButtonEventPayload('hovered', buttonId, sectionId, href, 'Apply now')}
    />
  );
};
